import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import { ListItemText } from '@mui/material';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteAdvancedPayment,
  getFilteredCalculatedAmount,
  getPaginatedAdvancedPayments,
  removeAdvancedPayment,
} from 'src/redux/slices/advanced-payments';

import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IAdvancedPaymentListItem,
  IAdvancedPaymentTableFilters,
  IAdvancedPaymentTableFilterValue,
  IAdvancedPaymentTableSort,
} from 'src/types/advanced-payment';

import AdvancedPaymentTableRow from '../advanced-payment-table-row';
import AdvancedPaymentsTableToolBar from '../advanced-payment-table-toolbar';

const TABLE_HEAD = [
  { id: 'employee', label: 'Employee Number', align: 'left', primary: true },
  { id: 'amount', label: 'Amount', align: 'right', primary: true },
  { id: 'paidDate', label: 'Paid Date', align: 'left', primary: true },
  { id: 'updatedAt', label: 'Updated', align: 'left', primary: true },
  { id: '', primary: true },
];

const defaultFilters: IAdvancedPaymentTableFilters = {
  generalSearch: '',
  startDate: null,
  endDate: null,
};

const defaultSortBy: IAdvancedPaymentTableSort = {
  sortBy: 'updatedAt',
  order: 'desc',
};

export default function AdvancedPaymentsListView() {
  const { status, payments, error } = useAppSelector((state) => state.advancedPayments);

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [calculatedTotal, setCalculatedTotal] = useState(0);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const handleFilters = useCallback(
    (name: string, value: IAdvancedPaymentTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string, employeeID: string) => {
      router.push(paths.dashboard.advancedPayments.edit(employeeID, id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedAdvancedPayments = useCallback(() => {
    dispatch(
      getPaginatedAdvancedPayments({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        sort: sort.order,
        sortBy: sort.sortBy,
        startDate: filters.startDate,
        endDate: filters.endDate,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const getFilteredCalculatedTotal = useCallback(async () => {
    try {
      const data = await dispatch(
        getFilteredCalculatedAmount({
          generalSearch: filters.generalSearch,
          startDate: filters.startDate,
          endDate: filters.endDate,
        })
      ).unwrap();

      setCalculatedTotal(data.total);
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
    }
  }, [dispatch, filters]);

  const handleDeleteRow = useCallback(
    async (payment: IAdvancedPaymentListItem, index: number) => {
      dispatch(removeAdvancedPayment({ paymentID: payment._id }));

      try {
        await dispatch(deleteAdvancedPayment({ payment, index })).unwrap();

        fetchPaginatedAdvancedPayments();
        enqueueSnackbar('Advanced Payment Deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedAdvancedPayments]
  );

  useEffect(() => {
    fetchPaginatedAdvancedPayments();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedAdvancedPayments]);

  useEffect(() => {
    if (canReset) {
      getFilteredCalculatedTotal();
    }
  }, [canReset, getFilteredCalculatedTotal]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Advanced Payments"
        links={[]}
        sx={{
          mb: { xs: 3 },
        }}
        action={
          <>
            {canReset && (
              <ListItemText
                primary="Total Value"
                secondary={fCurrencyRupees(calculatedTotal)}
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{
                  component: 'span',
                  color: 'text.disabled',
                }}
              />
            )}
          </>
        }
      />

      <Card>
        <AdvancedPaymentsTableToolBar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD.filter(({ primary }) => {
                  if (settings.themeLayout === 'vertical') {
                    return primary;
                  }
                  return true;
                })}
                rowCount={payments.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {payments?.items.map((row: IAdvancedPaymentListItem, index: number) => (
                      <AdvancedPaymentTableRow
                        key={row._id}
                        row={row}
                        selected={table.selected.includes(row._id)}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id, row.employee._id)}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && payments.count === 0 && (
                  <TableNoData
                    message="No Advanced Payments Found"
                    notFound={payments.count === 0}
                  />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={payments.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={(value) => {
            table.onChangeDense(value);
          }}
        />
      </Card>
    </Container>
  );
}
